import React, { useState } from "react";
import { Card, Button, Row, Col, Typography, Space, Badge, Tag, InputNumber, message } from "antd";
import { useNavigate } from "react-router-dom";
import { ShoppingCartOutlined, ReloadOutlined, PrinterOutlined } from "@ant-design/icons";
import {
  useFetchCategoriesQuery,
  useFetchProductsByCategoryQuery,
  useAddToPrintQueueMutation,
} from "./label.service";

const { Title, Text } = Typography;

export default function FoodDateLabel() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [quantities, setQuantities] = useState({});

  const { data: categoriesData, isLoading: categoriesLoading } = useFetchCategoriesQuery();
  const { data: productsData, isLoading: productsLoading } = useFetchProductsByCategoryQuery(
    selectedCategory?._id,
    { skip: !selectedCategory }
  );
  const [addToPrintQueue] = useAddToPrintQueueMutation();

  const categories = categoriesData?.data?.categories || [];
  const products = productsData?.data?.products || [];

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setQuantities({});
  };

  const handleQuantityChange = (productId, value) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: value || 0
    }));
  };

  const handleAddToQueue = async () => {
    const itemsToAdd = Object.entries(quantities)
      .filter(([_, qty]) => qty > 0)
      .map(([productId, quantity]) => ({ productId, quantity }));

    if (itemsToAdd.length === 0) {
      message.warning("Please select at least one item");
      return;
    }

    try {
      for (const item of itemsToAdd) {
        await addToPrintQueue(item).unwrap();
      }
      message.success(`${itemsToAdd.length} item(s) added to print queue`);
      setQuantities({});
    } catch (error) {
      message.error("Failed to add items to queue");
    }
  };

  const handleReset = () => {
    setQuantities({});
  };

  const totalItems = Object.values(quantities).reduce((sum, qty) => sum + qty, 0);

  return (
    <div style={{ padding: "24px" }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Title level={4}>Food Date Label</Title>
                <Badge count={totalItems} showZero>
                  <Button 
                    icon={<ShoppingCartOutlined />} 
                    onClick={() => navigate("/label/print-queue")}
                  >
                    Show Queue
                  </Button>
                </Badge>
              </div>

              <Text type="secondary">Choose category:</Text>
              
              <Row gutter={[16, 16]}>
                {categories.map(category => (
                  <Col xs={12} sm={8} md={6} lg={4} key={category._id}>
                    <Card
                      hoverable
                      onClick={() => handleCategorySelect(category)}
                      style={{
                        borderColor: selectedCategory?._id === category._id ? '#1890ff' : '#d9d9d9',
                        cursor: 'pointer',
                        textAlign: 'center',
                        background: selectedCategory?._id === category._id ? '#e6f7ff' : 'white'
                      }}
                    >
                      <Text strong style={{ fontSize: "16px" }}>{category.name}</Text>
                      <div>
                        <Tag color="blue" style={{ marginTop: 8 }}>
                          {category.productCount || 0} products
                        </Tag>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Space>
          </Card>
        </Col>

        {selectedCategory && (
          <Col span={24}>
            <Card>
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Title level={5}>{selectedCategory.name}</Title>
                  <Space>
                    <Button icon={<ReloadOutlined />} onClick={handleReset}>
                      RESET
                    </Button>
                    <Button type="primary" icon={<PrinterOutlined />} onClick={handleAddToQueue}>
                      ADD TO QUEUE ({totalItems})
                    </Button>
                  </Space>
                </div>

                <Row gutter={[16, 16]}>
                  {products.map(product => (
                    <Col xs={24} sm={12} md={8} key={product._id}>
                      <Card size="small" style={{ height: "100%" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <Text strong style={{ fontSize: "16px" }}>{product.name}</Text>
                            {product.customExpiryRule?.days && (
                              <div>
                                <Tag color="green" style={{ marginTop: 4 }}>
                                  {product.customExpiryRule.days} days
                                </Tag>
                              </div>
                            )}
                            {product.storageLocation && (
                              <div>
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                  Store: {product.storageLocation}
                                </Text>
                              </div>
                            )}
                          </div>
                          <div>
                            <InputNumber
                              min={0}
                              max={100}
                              value={quantities[product._id] || 0}
                              onChange={(value) => handleQuantityChange(product._id, value)}
                              addonBefore="-"
                              addonAfter="+"
                              style={{ width: 120 }}
                            />
                          </div>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Space>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
}